import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Row, Col, FormGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

import { addClickRecord, getCaseId, getClientId, getToken } from "../../Utils/helper";
import { api_without_cancellation } from "../../api/api";
import { fetchCaseSummary } from "../../api/case";
import { setCaseSummary } from "../../Redux/caseData/caseDataSlice";
import SelectStateModal from "../TreatmentPage/modals/state-modal/SelectStateModal";

const formatCaseClientAddress = (address) =>
  [
    address?.address1,
    address?.address2,
    address?.city,
    [address?.state, address?.post_code].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

const INITIAL_CLIENT_DATA = {
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  dob: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  states: "",
  post_code: "",
  insurance_type: "",
  source_name: "",
  source: "",
  source_type: "",
  ad_date: "",
  ad_time: "",
  ad_location: "",
  ad_location_address1: "",
  ad_location_address2: "",
  ad_location_city: "",
  ad_location_state: "",
  ad_location_zip: "",
  plaintiff_insurance: "",
  policy_number: "",
  claim_number: "",
  intake_notes: "",
};

const AddNewClientModal = ({ setOpen, open }) => {
  const currentCase = useSelector((state) => state?.caseData?.current);
  const caseSummary = useSelector((state) => state?.caseData?.summary);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const saveInProgressRef = useRef(false);
  const addressDropdownRef = useRef(null);
  const [caseClientAddresses, setCaseClientAddresses] = useState([]);
  const [selectedCaseAddress, setSelectedCaseAddress] = useState(null);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(false);

  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";
  const token = getToken();
  const [statesAbrs, setStatesAbrs] = useState([]);
  const [stateShow, setStateShow] = useState(false);
  const [adLocationStateShow, setAdLocationStateShow] = useState(false);

  const fetchStatesData = useCallback(async () => {
    try {
      const response = await axios.get(`${origin}/api/states/`, {
        headers: { Authorization: token },
      });
      if (response.status === 200) {
        setStatesAbrs(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch states:", error);
    }
  }, [origin, token]);

  useEffect(() => {
    fetchStatesData();
  }, [fetchStatesData]);

  useEffect(() => {
    setCaseClientAddresses([]);
    setSelectedCaseAddress(null);
    setAddressDropdownOpen(false);

    if (!open || !currentCase?.id) {
      setAddressesLoading(false);
      return undefined;
    }

    let ignoreResponse = false;

    const fetchCaseClientAddresses = async () => {
      setAddressesLoading(true);

      try {
        const response = await api_without_cancellation.get(
          `/api/case-client-addresses/${currentCase.id}`
        );

        if (!ignoreResponse) {
          setCaseClientAddresses(
            Array.isArray(response.data?.addresses) ? response.data.addresses : []
          );
        }
      } catch (error) {
        if (!ignoreResponse) {
          console.error("Failed to fetch case client addresses:", error);
          setCaseClientAddresses([]);
        }
      } finally {
        if (!ignoreResponse) {
          setAddressesLoading(false);
        }
      }
    };

    fetchCaseClientAddresses();

    return () => {
      ignoreResponse = true;
    };
  }, [currentCase?.id, open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressDropdownRef.current && !addressDropdownRef.current.contains(event.target)) {
        setAddressDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [clientData, setClientData] = useState(() => ({ ...INITIAL_CLIENT_DATA }));
  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCaseAddressSelect = (address) => {
    setSelectedCaseAddress(address);
    setAddressDropdownOpen(false);
    setClientData((previousData) => ({
      ...previousData,
      address1: address.address1 || "",
      address2: address.address2 || "",
      city: address.city || "",
      states: address.state || "",
      post_code: address.post_code || "",
    }));
  };

  const handleClear = () => {
    setShowConfirmation(false);
    setClientData({ ...INITIAL_CLIENT_DATA });
    setOpen(false);
    addClickRecord(999, 1);
  };

  const handleSave = async () => {
    if (saveInProgressRef.current) return;

    saveInProgressRef.current = true;
    setLoading(true);
    setShowConfirmation(true);
    addClickRecord(999, 1);

    try {
      const clientId = getClientId();
      const caseId = getCaseId();

      await api_without_cancellation.post(
        `/api/newClientExistingCase/${clientId}/${caseId}/`,
        clientData
      );

      toast.success("New Client Added to the case successfully");
      handleClear();

      try {
        const data = await fetchCaseSummary(clientId, caseId);
        dispatch(setCaseSummary(data));
      } catch (summaryError) {
        console.error("Failed to refresh the case summary:", summaryError);
      }
    } catch (error) {
      console.error("Failed to add the client:", error);
      setShowConfirmation(false);
      toast.error(
        error?.response?.data?.detail ||
          error?.detail ||
          "Something Occured! Please Try Again Later"
      );
    } finally {
      saveInProgressRef.current = false;
      setLoading(false);
    }
  };

  const formatPhoneNumberForDisplay = (value) => {
    if (!value) return "";
    const phoneNumber = value.replace(/[^\d]/g, "").slice(0, 10);

    if (phoneNumber.length < 4) return `(${phoneNumber}`;
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  };
  const handlePhoneChange = (e) => {
    const rawDigits = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    setClientData({
      ...clientData,
      phone: formatPhoneNumberForDisplay(rawDigits),
    });
  };

  const savingMessages = [
    "New client is being saved. Please wait...",
    "Validating client details...",
    "Creating client record...",
    "Almost done...",
  ];

  const [savingMsgIndex, setSavingMsgIndex] = useState(0);

  useEffect(() => {
    if (!(open && showConfirmation)) {
      setSavingMsgIndex(0);
      return;
    }

    const id = setInterval(() => {
      setSavingMsgIndex((i) => Math.min(i + 1, savingMessages.length - 1));
    }, 1000);

    return () => clearInterval(id);
  }, [open, showConfirmation]);

  return [
    <Modal
      key="add-client-form"
      show={open && !showConfirmation}
      onHide={handleClear}
      dialogClassName="modal-dialog-3-max-width"
      centered
    >
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
      >
        <Modal.Title
          className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
          style={{ fontSize: "14px", fontWeight: "600" }}
        >
          Add New Client for Existing Case
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{
          padding: "5px",
        }}
      >
        {currentCase && (
          <div className="w-100 m-b-5">
            <p
              className="d-flex align-items-center"
              style={{
                gap: "5px",
              }}
            >
              {currentCase.case_type?.name} Case DOI: with{" "}
              {currentCase.for_client?.first_name || currentCase.for_client?.entity_name}{" "}
              {currentCase.for_client?.last_name || ""}
              {caseSummary?.multipleClient_for_case?.map((client) => (
                <span>
                  , {client.new_client.first_name || client.new_client.entity_name || ""}{" "}
                  {client.new_client.last_name}
                </span>
              ))}
            </p>
          </div>
        )}
        {currentCase && (
          <Row className="mx-auto m-b-5 m-t-5">
            <Col sm="12" className="ps-0 pe-0">
              <FormGroup>
                <div ref={addressDropdownRef} className="custom-select-new-provider">
                  <div
                    className={`dropdown-button rounded-0 height-25 p-l-5 form-control d-flex align-items-center ${
                      selectedCaseAddress ? "has-value" : ""
                    }`}
                    role="button"
                    tabIndex={addressesLoading || caseClientAddresses.length === 0 ? -1 : 0}
                    aria-haspopup="listbox"
                    aria-expanded={addressDropdownOpen}
                    aria-disabled={addressesLoading || caseClientAddresses.length === 0}
                    onClick={() => {
                      if (!addressesLoading && caseClientAddresses.length > 0) {
                        setAddressDropdownOpen((isOpen) => !isOpen);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (
                        (event.key === "Enter" || event.key === " ") &&
                        !addressesLoading &&
                        caseClientAddresses.length > 0
                      ) {
                        event.preventDefault();
                        setAddressDropdownOpen((isOpen) => !isOpen);
                      }
                    }}
                    style={{
                      paddingRight: "35px",
                      cursor:
                        addressesLoading || caseClientAddresses.length === 0
                          ? "default"
                          : "pointer",
                    }}
                  >
                    <span className="text-truncate">
                      {addressesLoading
                        ? "Loading addresses from this case..."
                        : selectedCaseAddress
                          ? formatCaseClientAddress(selectedCaseAddress) ||
                            "No address details available"
                          : caseClientAddresses.length > 0
                            ? "Select an existing client address"
                            : "No client addresses are available for this case"}
                    </span>
                  </div>

                  {addressDropdownOpen && caseClientAddresses.length > 0 && (
                    <div
                      className="dropdown-menu p-0 mt-0"
                      role="listbox"
                      style={{
                        display: "block",
                        width: "100%",
                        border: "1px solid #ced4da",
                        maxHeight: "220px",
                        minHeight: "220px",
                        overflowY: "auto",
                        zIndex: 9999,
                      }}
                    >
                      {caseClientAddresses.map((address, index) => (
                        <div
                          key={`${address.client_id || "client"}-${index}`}
                          className="dropdown-option"
                          role="option"
                          aria-selected={selectedCaseAddress === address}
                          tabIndex={0}
                          onClick={() => handleCaseAddressSelect(address)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleCaseAddressSelect(address);
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            paddingLeft: "5px",
                            borderBottom:
                              index < caseClientAddresses.length - 1 ? "1px solid #e9ecef" : "none",
                          }}
                        >
                          <div
                            className="height-25 d-flex align-items-center"
                            style={{ fontSize: "14px", color: "black" }}
                          >
                            {formatCaseClientAddress(address) || "No address details available"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FormGroup>
            </Col>
          </Row>
        )}
        <div className="row">
          <div className="col-md-6 p-r-5">
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>First Name:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0 ps-0 pe-0">
                <input
                  type="text"
                  name="first_name"
                  value={clientData.first_name}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="First Name"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Middle Name:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0 ps-0 pe-0">
                <input
                  type="text"
                  name="middle_name"
                  value={clientData.middle_name}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Middle Name"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Last Name:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="last_name"
                  value={clientData.last_name}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Last Name"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>DOB:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="date"
                  name="dob"
                  value={clientData.dob}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  min="1000-01-01"
                  max="9999-12-31"
                  placeholder="DOB"
                  id="date"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Phone:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="phone"
                  value={clientData.phone}
                  onChange={handlePhoneChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Phone"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Email:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="email"
                  name="email"
                  value={clientData.email}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Email"
                />
              </div>
            </div>

            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Address:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="address1"
                  value={clientData.address1}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Address 1"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0" />
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="address2"
                  value={clientData.address2}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Address 2"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0" />
              <div className="col-md-4 ps-0 p-r-5">
                <input
                  type="text"
                  name="city"
                  value={clientData.city}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="City"
                />
              </div>
              <div className="col-md-3 ps-0 p-r-5">
                <div
                  className="dropdown-button custom-select-new-provider rounded-0 p-0 p-l-5 form-control p-l-5 p-r-5 height-25 d-flex align-items-center"
                  onClick={() => setStateShow(true)}
                  style={{ cursor: "pointer" }}
                >
                  {clientData.states || "Select State"}
                </div>
              </div>
              <div className="col-md-2 ps-0 pe-0">
                <input
                  type="text"
                  name="post_code"
                  value={clientData.post_code}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="ZIP"
                />
              </div>
            </div>

            <div className="row m-b-5 mx-0 p-0 align-items-start">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Intake Notes:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <textarea
                  cols="30"
                  name="intake_notes"
                  value={clientData.intake_notes}
                  onChange={handleChange}
                  style={{
                    minHeight: "60px",
                  }}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Intake Notes"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Plaintiff Insurance:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="plaintiff_insurance"
                  value={clientData.plaintiff_insurance}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Plaintiff insurance company"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Policy #:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="policy_number"
                  value={clientData.policy_number}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Policy #"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Claim #:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="claim_number"
                  value={clientData.claim_number}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Claim #"
                />
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Source Generally:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="source"
                  value={clientData.source}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="How Did You Hear About Us?"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Source Type:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <select
                  name="source_type"
                  value={clientData.source_type}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                >
                  <option value="CT1">Case Type 1</option>
                  <option value="CT2">Case Type 2</option>
                  <option value="CT3">Case Type 3</option>
                </select>
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Source Name:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="source_name"
                  value={clientData.source_name}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Source Name"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Ad date:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <div className="row align-items-center">
                  <div className="col-md-5">
                    <input
                      placeholder="Ad date"
                      className="form-control p-l-5 p-r-5 py-0 height-25"
                      min="1000-01-01"
                      max="9999-12-31"
                      id="date"
                      type="date"
                      name="ad_date"
                      value={clientData.ad_date}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-2">
                    <p style={{ color: "var(--primary-25)" }}>Time:</p>
                  </div>
                  <div className="col-md-5">
                    <input
                      type="time"
                      name="ad_time"
                      value={clientData.ad_time}
                      onChange={handleChange}
                      className="form-control p-l-5 p-r-5 py-0 height-25"
                      placeholder="Ad Time"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Ad location:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="ad_location"
                  value={clientData.ad_location}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Ad Location"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0">
                <p style={{ color: "var(--primary-25)" }}>Ad Location Addr:</p>
              </div>
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="ad_location_address1"
                  value={clientData.ad_location_address1}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Ad Location Address 1"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0" />
              <div className="col-md-9 ps-0 pe-0">
                <input
                  type="text"
                  name="ad_location_address2"
                  value={clientData.ad_location_address2}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Ad Location Address 2"
                />
              </div>
            </div>
            <div className="row m-b-5 mx-0 p-0 align-items-center">
              <div className="col-md-3 ps-0 pe-0" />
              <div className="col-md-4 ps-0 p-r-5">
                <input
                  type="text"
                  name="ad_location_city"
                  value={clientData.ad_location_city}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Ad City"
                />
              </div>
              <div className="col-md-3 ps-0 p-r-5 ">
                <div
                  className="dropdown-button custom-select-new-provider rounded-0 p-0 p-l-5 form-control p-l-5 p-r-5 height-25 d-flex align-items-center"
                  onClick={() => setAdLocationStateShow(true)}
                  style={{ cursor: "pointer" }}
                >
                  {clientData.ad_location_state || "Select State"}
                </div>
              </div>
              <div className="col-md-2 ps-0 pe-0">
                <input
                  type="text"
                  name="ad_location_zip"
                  value={clientData.ad_location_zip}
                  onChange={handleChange}
                  className="form-control p-l-5 p-r-5 py-0 height-25"
                  placeholder="Ad ZIP"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider"
        style={{ borderTop: "none" }}
      >
        <Button
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          variant="secondary"
          onClick={handleClear}
        >
          Close
        </Button>
        <Button
          variant="success"
          disabled={loading}
          onClick={handleSave}
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
        >
          {loading ? "Saving..." : "Save and Close"}
        </Button>
      </Modal.Footer>
      {stateShow && (
        <SelectStateModal
          show={stateShow}
          handleClose={() => setStateShow(false)}
          onChange={(state) => {
            setClientData((prev) => ({ ...prev, states: state.StateAbr }));
            setStateShow(false);
          }}
          statesData={statesAbrs}
        />
      )}
      {adLocationStateShow && (
        <SelectStateModal
          show={adLocationStateShow}
          handleClose={() => setAdLocationStateShow(false)}
          onChange={(state) => {
            setClientData((prev) => ({ ...prev, ad_location_state: state.StateAbr }));
            setAdLocationStateShow(false);
          }}
          statesData={statesAbrs}
        />
      )}
    </Modal>,

    <Modal
      key="add-client-confirmation"
      show={open && showConfirmation}
      onHide={() => {}}
      backdrop="static"
      keyboard={false}
      size="md"
      centered
    >
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
      >
        <Modal.Title
          className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
          style={{ fontSize: "14px", fontWeight: "600" }}
        >
          Saving New Client
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "100px", gap: "10px" }}
        >
          <span className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Saving...</span>
          </span>
          <p className="m-0 text-center font-weight-semibold" style={{ fontSize: "1rem" }}>
            {savingMessages[savingMsgIndex]}
          </p>
        </div>
      </Modal.Body>
    </Modal>,
  ];
};

export default AddNewClientModal;
